import { inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, of, switchMap, tap } from 'rxjs';

import { AuthService } from '../../../auth/services/auth/auth.service';
import { OnboardingStepPayloadService } from '../../shared/services/onboarding-step-payload.service';
import type { IOnboardingResponse } from '../../shared/models/onboarding-common.interface';
import type { IErrorResponse } from '../../shared/models/onboarding-common.interface';
import { OnboardingApiService } from '../../shared/services/onboarding-api.service';
import {
  SPONSOR_ONBOARDING_AUDIENCE_STEP_NUMBER,
  SPONSOR_ONBOARDING_BUDGET_DURATION_STEP_NUMBER,
  SPONSOR_ONBOARDING_COMPANY_ACTIONS_STEP_NUMBER,
  SPONSOR_ONBOARDING_ERROR_TRANSLATION_KEYS,
  SPONSOR_ONBOARDING_GEOGRAPHY_STEP_NUMBER,
  SPONSOR_ONBOARDING_GOALS_STEP_NUMBER,
  SPONSOR_ONBOARDING_PREFERENCES_STEP_NUMBER,
  SPONSOR_ONBOARDING_SECTOR_STEP_NUMBER,
  SPONSOR_ONBOARDING_STEP_IDS,
  SPONSOR_SECTOR_OTHER_VALUE,
} from '../constants/sponsor-onboarding.constants';
import type { ISponsorOnboardingDraft } from '../models/sponsor-onboarding-draft.interface';
import type { ISponsorOnboardingSaveResult } from '../models/sponsor-onboarding-save-result.interface';
import { SponsorGeographyStepService } from './sponsor-geography-step.service';
import { SponsorOnboardingDraftService } from './sponsor-onboarding-draft.service';
import { SponsorOnboardingHydrateService } from './sponsor-onboarding-hydrate.service';

@Injectable({
  providedIn: 'root',
})
export class SponsorOnboardingSaveService {
  private readonly onboardingApi = inject(OnboardingApiService);
  private readonly authService = inject(AuthService);
  private readonly stepPayload = inject(OnboardingStepPayloadService);
  private readonly geographyStep = inject(SponsorGeographyStepService);
  private readonly draftService = inject(SponsorOnboardingDraftService);
  private readonly hydrateService = inject(SponsorOnboardingHydrateService);

  private readonly _saving = signal<boolean>(false);
  private readonly _redirectingToDashboard = signal<boolean>(false);

  public readonly saving = this._saving.asReadonly();
  public readonly redirectingToDashboard = this._redirectingToDashboard.asReadonly();

  public saveGoalsStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveStep(
      SPONSOR_ONBOARDING_GOALS_STEP_NUMBER,
      SPONSOR_ONBOARDING_STEP_IDS.goals,
      this.buildGoalsStepData(),
    );
  }

  public saveAudienceStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveStep(
      SPONSOR_ONBOARDING_AUDIENCE_STEP_NUMBER,
      SPONSOR_ONBOARDING_STEP_IDS.audience,
      this.buildAudienceStepData(),
    );
  }

  public saveGeographyStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveStep(
      SPONSOR_ONBOARDING_GEOGRAPHY_STEP_NUMBER,
      SPONSOR_ONBOARDING_STEP_IDS.geography,
      this.buildGeographyStepData(),
    );
  }

  public saveSectorStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveStep(
      SPONSOR_ONBOARDING_SECTOR_STEP_NUMBER,
      SPONSOR_ONBOARDING_STEP_IDS.sector,
      this.buildSectorStepData(),
    );
  }

  public saveCampaignBudgetStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveStep(
      SPONSOR_ONBOARDING_BUDGET_DURATION_STEP_NUMBER,
      SPONSOR_ONBOARDING_STEP_IDS.campaignBudget,
      this.buildCampaignBudgetStepData(),
    );
  }

  public saveCompanyActionsStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveStep(
      SPONSOR_ONBOARDING_COMPANY_ACTIONS_STEP_NUMBER,
      SPONSOR_ONBOARDING_STEP_IDS.companyActions,
      this.buildCompanyActionsStepData(),
    );
  }

  public savePreferencesStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveStep(
      SPONSOR_ONBOARDING_PREFERENCES_STEP_NUMBER,
      SPONSOR_ONBOARDING_STEP_IDS.preferences,
      this.buildPreferencesStepData(),
    );
  }

  public finalizeOnboarding(): Observable<IOnboardingResponse> {
    this._saving.set(true);
    return this.onboardingApi.finish().pipe(
      tap((response) => {
        const token = this.readRefreshedTokenFromOnboardingResponse(response);
        if (token) {
          this.authService.applyRefreshedAuthToken(token);
        }
      }),
      switchMap((response) => {
        if (!response.success) {
          return of(response);
        }
        this._redirectingToDashboard.set(true);
        this.draftService.storeExplorerDefaultsBeforeCompletion();
        this.draftService.clearDraftAfterCompletion();
        this.hydrateService.markCompletedAfterFinish();
        return this.authService.refreshOnboardingStatus().pipe(map(() => response));
      }),
      finalize(() => this._saving.set(false)),
    );
  }

  public clearRedirectingToDashboard(): void {
    this._redirectingToDashboard.set(false);
  }

  public resolveErrorTranslationKey(err: IErrorResponse, fallbackKey: string): string {
    if (this.isFastifySchemaValidationError(err)) {
      return 'onboarding.sponsorOnboarding.errors.invalidOnboardingData';
    }
    const backendMessage = this.extractBackendErrorMessage(err);
    if (!backendMessage) {
      return fallbackKey;
    }
    return (
      SPONSOR_ONBOARDING_ERROR_TRANSLATION_KEYS[backendMessage] ??
      `onboarding.sponsorOnboarding.errors.${backendMessage}`
    );
  }

  public buildGoalsStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    const goals = (draft ?? this.draftService.draft()).goals;
    return {
      company_name: goals.company_name.trim(),
      primary_goal: goals.primary_goal,
      secondary_goal: goals.secondary_goal,
    };
  }

  public buildAudienceStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    const audience = (draft ?? this.draftService.draft()).audience;
    return {
      primary_audience: audience.primary_audience,
      secondary_audience: audience.secondary_audience,
    };
  }

  public buildGeographyStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    const geography = (draft ?? this.draftService.draft()).geography;
    const payload = this.geographyStep.buildStepPayload(geography);
    if (!payload) {
      return this.stepPayload.compactStepData({
        geographic_radius: geography.geographic_radius,
      });
    }
    return this.stepPayload.compactStepData(payload);
  }

  public buildSectorStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    const sector = (draft ?? this.draftService.draft()).sector;
    return {
      sector: sector.sector,
      sector_other:
        sector.sector === SPONSOR_SECTOR_OTHER_VALUE
          ? this.draftService.toNullableTrimmedString(sector.sector_other)
          : null,
    };
  }

  public buildCampaignBudgetStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    const budget = (draft ?? this.draftService.draft()).campaign_budget;
    return {
      budget_range: budget.budget_range,
      campaign_duration: budget.campaign_duration,
    };
  }

  public buildCompanyActionsStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    const actions = (draft ?? this.draftService.draft()).company_actions.company_actions;
    return {
      company_actions: [...actions],
    };
  }

  public buildPreferencesStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    const preferences = (draft ?? this.draftService.draft()).preferences;
    const payload: Record<string, unknown> = {};
    if (preferences.sports_interest.length > 0) {
      payload['sports_interest'] = [...preferences.sports_interest];
    }
    if (preferences.avoid_preferences.length > 0) {
      payload['avoid_preferences'] = [...preferences.avoid_preferences];
    }
    return payload;
  }

  public readRefreshedTokenFromOnboardingResponse(response: IOnboardingResponse): string | null {
    const data = response.data;
    if (!data || typeof data !== 'object') {
      return null;
    }

    const token = (data as { token?: unknown }).token;
    return typeof token === 'string' && token.trim().length > 0 ? token.trim() : null;
  }

  public saveStep(
    stepNumber: number,
    stepId: string,
    stepData: Record<string, unknown>,
  ): Observable<ISponsorOnboardingSaveResult> {
    this._saving.set(true);
    return this.onboardingApi
      .saveStep({
        step_number: stepNumber,
        step_id: stepId,
        step_data: this.stepPayload.compactStepData(stepData),
      })
      .pipe(
        tap((response) => {
          if (response.success) {
            this.hydrateService.applyStatusResponse(response);
          }
        }),
        map((response) => ({
          success: response.success,
          errorTranslationKey: response.success
            ? undefined
            : 'onboarding.sponsorOnboarding.errors.saveFailed',
        })),
        catchError((err: IErrorResponse) =>
          of({
            success: false,
            errorTranslationKey: this.resolveErrorTranslationKey(
              err,
              'onboarding.sponsorOnboarding.errors.saveFailed',
            ),
          }),
        ),
        finalize(() => this._saving.set(false)),
      );
  }

  private isFastifySchemaValidationError(err: IErrorResponse): boolean {
    const statusCode = err['statusCode'];
    const message = this.extractFastifyErrorMessage(err);
    if (typeof statusCode === 'number' && statusCode === 400 && message !== null) {
      return message.includes('body/step_data') || message.includes('step_data/');
    }
    if (message !== null) {
      return message.includes('body/step_data') || message.includes('step_data/');
    }
    return false;
  }

  private extractFastifyErrorMessage(err: IErrorResponse): string | null {
    const topLevelMessage = err['message'];
    if (typeof topLevelMessage === 'string' && topLevelMessage.length > 0) {
      return topLevelMessage;
    }
    return null;
  }

  public extractBackendErrorMessage(err: IErrorResponse): string | null {
    const topLevelError = err.error;
    if (!topLevelError || typeof topLevelError !== 'object') {
      return null;
    }
    if (typeof topLevelError.message === 'string' && topLevelError.message.length > 0) {
      return topLevelError.message;
    }
    const nestedError = topLevelError['error'];
    if (
      nestedError &&
      typeof nestedError === 'object' &&
      'message' in nestedError &&
      typeof nestedError.message === 'string' &&
      nestedError.message.length > 0
    ) {
      return nestedError.message;
    }
    return null;
  }
}
